import React, { useState } from 'react';
import { X, Building2, Mail, Phone, MapPin, User, Send, CheckCircle, AlertTriangle, XCircle, Search, Loader2 } from 'lucide-react';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { consultarCNPJ, validarCNPJ } from '../services/cnpjService';
import { consultarCEP, validarCEP } from '../services/cepService';

const NovaEmpresaModal = ({ isOpen, onClose, onSuccess, initialData = {} }) => {
  const [formData, setFormData] = useState({
    // Dados da empresa (com dados iniciais do lead, se houver)
    nome_empresa: initialData.nome_empresa || '',
    nome_fantasia: initialData.nome_fantasia || '',
    cnpj: initialData.cnpj || '',
    email: initialData.email_empresa || '',
    email_financeiro: initialData.email_financeiro || '',
    telefone: initialData.telefone || '',
    endereco: {
      logradouro: initialData.endereco?.logradouro || '',
      numero: initialData.endereco?.numero || '',
      complemento: initialData.endereco?.complemento || '',
      bairro: initialData.endereco?.bairro || '',
      cidade: initialData.endereco?.cidade || '',
      estado: initialData.endereco?.estado || '',
      cep: initialData.endereco?.cep || ''
    },
    responsavel_legal: initialData.responsavel_legal || '',
    inscricao_estadual: initialData.inscricao_estadual || '',
    inscricao_municipal: initialData.inscricao_municipal || '',
    // Dados do admin (com dados do lead, se houver)
    admin_nome: initialData.admin_nome || '',
    admin_email: initialData.admin_email || ''
  });

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [etapaAtual, setEtapaAtual] = useState(1); // 1: Empresa, 2: Confirmação
  
  // ✅ NOVO: Estados para consultas externas
  const [consultandoCNPJ, setConsultandoCNPJ] = useState(false);
  const [erroCNPJ, setErroCNPJ] = useState('');
  const [consultandoCEP, setConsultandoCEP] = useState(false);
  const [erroCEP, setErroCEP] = useState('');

  // ✅ NOVO: Atualizar formData quando initialData mudar (conversão de lead)
  React.useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        nome_empresa: initialData.nome_empresa || '',
        nome_fantasia: initialData.nome_fantasia || '',
        cnpj: initialData.cnpj || '',
        email: initialData.email_empresa || '',
        email_financeiro: initialData.email_financeiro || '',
        telefone: initialData.telefone || '',
        endereco: {
          logradouro: initialData.endereco?.logradouro || '',
          numero: initialData.endereco?.numero || '',
          complemento: initialData.endereco?.complemento || '',
          bairro: initialData.endereco?.bairro || '',
          cidade: initialData.endereco?.cidade || '',
          estado: initialData.endereco?.estado || '',
          cep: initialData.endereco?.cep || ''
        },
        responsavel_legal: initialData.responsavel_legal || '',
        inscricao_estadual: initialData.inscricao_estadual || '',
        inscricao_municipal: initialData.inscricao_municipal || '',
        admin_nome: initialData.admin_nome || '',
        admin_email: initialData.admin_email || ''
      });
    }
  }, [initialData]);

  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('endereco.')) {
      const enderecoField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          [enderecoField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return numbers.replace(/(\d{2})(\d{0,3})/, '$1.$2');
    if (numbers.length <= 8) return numbers.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
    if (numbers.length <= 12) return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const formatTelefone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return numbers.replace(/(\d{5})(\d{0,3})/, '$1-$2');
  };

  // ✅ NOVA FUNÇÃO: Consultar CNPJ na Receita Federal
  const handleConsultarCNPJ = async () => {
    const cnpjLimpo = formData.cnpj.replace(/\D/g, '');
    
    if (cnpjLimpo.length !== 14) {
      setErroCNPJ('CNPJ deve ter 14 dígitos');
      return;
    }

    if (!validarCNPJ(formData.cnpj)) {
      setErroCNPJ('CNPJ inválido');
      return;
    }

    setConsultandoCNPJ(true);
    setErroCNPJ('');

    try {
      const resultado = await consultarCNPJ(formData.cnpj);
      
      console.log('🔍 Resultado completo da consulta CNPJ:', resultado);
      console.log('🔍 Dados recebidos:', resultado.data);
      
      if (resultado.success && resultado.data) {
        console.log('📝 Preenchendo campos com os dados do CNPJ...');
        
        // ✅ CORREÇÃO: Usar a mesma estrutura do código inline anterior
        setFormData(prev => ({
          ...prev,
          nome_empresa: resultado.data.nome_empresa || prev.nome_empresa,
          nome_fantasia: resultado.data.nome_fantasia || prev.nome_fantasia,
          telefone: resultado.data.telefone || prev.telefone,
          endereco: {
            ...prev.endereco,
            logradouro: resultado.data.endereco?.logradouro || prev.endereco.logradouro,
            numero: resultado.data.endereco?.numero || prev.endereco.numero,
            complemento: resultado.data.endereco?.complemento || prev.endereco.complemento,
            bairro: resultado.data.endereco?.bairro || prev.endereco.bairro,
            cidade: resultado.data.endereco?.cidade || prev.endereco.cidade,
            estado: resultado.data.endereco?.estado || prev.endereco.estado,
            cep: resultado.data.endereco?.cep ? 
              resultado.data.endereco.cep.replace(/[^\d]/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2') : 
              prev.endereco.cep
          },
          inscricao_estadual: resultado.data.inscricao_estadual || prev.inscricao_estadual
        }));
        
        console.log('✅ Campos preenchidos com sucesso!');
      } else {
        setErroCNPJ(resultado.message || 'Erro ao consultar CNPJ');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar CNPJ:', error);
      setErroCNPJ('Erro ao consultar CNPJ. Tente novamente.');
    } finally {
      setConsultandoCNPJ(false);
    }
  };

  // ✅ NOVA FUNÇÃO: Consultar CEP via ViaCEP
  const handleConsultarCEP = async () => {
    const cepLimpo = formData.endereco.cep.replace(/\D/g, '');
    
    if (cepLimpo.length !== 8) {
      setErroCEP('CEP deve ter 8 dígitos');
      return;
    }

    if (!validarCEP(formData.endereco.cep)) {
      setErroCEP('CEP inválido');
      return;
    }

    setConsultandoCEP(true);
    setErroCEP('');

    try {
      const resultado = await consultarCEP(formData.endereco.cep);
      
      console.log('🔍 Resultado completo da consulta CEP:', resultado);
      console.log('🔍 Dados recebidos:', resultado.data);
      
      if (resultado.success && resultado.data) {
        console.log('📝 Preenchendo campos com os dados do CEP...');
        
        setFormData(prev => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            logradouro: resultado.data.logradouro || prev.endereco.logradouro,
            bairro: resultado.data.bairro || prev.endereco.bairro,
            cidade: resultado.data.cidade || prev.endereco.cidade,
            estado: resultado.data.estado || prev.endereco.estado,
            complemento: resultado.data.complemento || prev.endereco.complemento
          }
        }));
        
        console.log('✅ Campos do CEP preenchidos com sucesso!');
      } else {
        setErroCEP(resultado.message || 'Erro ao consultar CEP');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar CEP:', error);
      setErroCEP('Erro ao consultar CEP. Tente novamente.');
    } finally {
      setConsultandoCEP(false);
    }
  };

  const validarEtapa1 = () => {
    const erros = [];
    
    if (!formData.nome_empresa.trim()) erros.push('Nome da empresa é obrigatório');
    if (!formData.cnpj.trim()) erros.push('CNPJ é obrigatório');
    if (!formData.email.trim()) erros.push('Email da empresa é obrigatório');
    if (!formData.telefone.trim()) erros.push('Telefone é obrigatório');
    if (!formData.admin_nome.trim()) erros.push('Nome do administrador é obrigatório');
    if (!formData.admin_email.trim()) erros.push('Email do administrador é obrigatório');
    if (!formData.endereco.logradouro.trim()) erros.push('Logradouro é obrigatório');
    if (!formData.endereco.numero.trim()) erros.push('Número é obrigatório');
    if (!formData.endereco.bairro.trim()) erros.push('Bairro é obrigatório');
    if (!formData.endereco.cidade.trim()) erros.push('Cidade é obrigatória');
    if (!formData.endereco.estado) erros.push('Estado é obrigatório');
    if (!formData.endereco.cep.trim()) erros.push('CEP é obrigatório');
    
    // Validar formato do email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      erros.push('Email da empresa inválido');
    }
    if (formData.admin_email && !emailRegex.test(formData.admin_email)) {
      erros.push('Email do administrador inválido');
    }

    // Validar CNPJ (básico)
    const cnpjNumbers = formData.cnpj.replace(/\D/g, '');
    if (cnpjNumbers.length !== 14) {
      erros.push('CNPJ deve ter 14 dígitos');
    }

    // Validar CEP
    const cepNumbers = formData.endereco.cep.replace(/\D/g, '');
    if (cepNumbers.length !== 8) {
      erros.push('CEP deve ter 8 dígitos');
    }

    if (erros.length > 0) {
      setErro(erros.join(', '));
      return false;
    }

    setErro('');
    return true;
  };


  const proximaEtapa = () => {
    if (etapaAtual === 1 && validarEtapa1()) {
      setEtapaAtual(2);
    }
  };

  const etapaAnterior = () => {
    if (etapaAtual > 1) {
      setEtapaAtual(etapaAtual - 1);
      setErro('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ Evitar submit automático se não estiver na etapa final
    if (etapaAtual < 2) {
      console.log('⚠️ Submit bloqueado - ainda não está na etapa final');
      return;
    }
    
    if (!validarEtapa1()) {
      return;
    }

    try {
      setLoading(true);
      setErro('');

      console.log('🔍 Criando empresa com dados:', formData);

      // ✅ CORREÇÃO: Preparar dados no formato esperado pelo backend
      const dadosEmpresa = {
        nome_empresa: formData.nome_empresa.trim(),
        nome_fantasia: formData.nome_fantasia?.trim() || '',
        cnpj: formData.cnpj.replace(/\D/g, ''),
        email_empresa: formData.email.trim(),
        email_financeiro: formData.email_financeiro?.trim() || '',
        telefone: formData.telefone.replace(/\D/g, ''),
        endereco: {
          rua: formData.endereco.logradouro.trim(),
          numero: formData.endereco.numero.trim(),
          complemento: formData.endereco.complemento?.trim() || '',
          bairro: formData.endereco.bairro.trim(),
          cidade: formData.endereco.cidade.trim(),
          estado: formData.endereco.estado,
          cep: formData.endereco.cep.replace(/\D/g, '')
        },
        responsavel_legal: formData.responsavel_legal?.trim() || '',
        inscricao_estadual: formData.inscricao_estadual?.trim() || '',
        inscricao_municipal: formData.inscricao_municipal?.trim() || '',
        admin_nome: formData.admin_nome.trim(),
        admin_email: formData.admin_email.trim()
      };

      console.log('🔍 Dados formatados para backend:', dadosEmpresa);

      const createCompany = httpsCallable(functions, 'createCompany');
      const result = await createCompany(dadosEmpresa);

      console.log('✅ Resultado da criação:', result.data);

      if (result.data.success) {
        const data = result.data.data;
        
        // Determinar tipo de mensagem baseado no resultado
        const messageType = data.convite_enviado ? 'success' : 'warning';
        const message = data.message;
        const warning = data.warning;

        onSuccess({
          type: messageType,
          message: message,
          warning: warning,
          company: {
            id: data.empresa_id,
            nome: data.nome_empresa,
            admin_email: data.admin_email,
            convite_enviado: data.convite_enviado
          }
        });
        
        onClose();
        
        // Reset form
        setFormData({
          nome_empresa: '',
          nome_fantasia: '',
          cnpj: '',
          email: '',
          email_financeiro: '',
          telefone: '',
          endereco: {
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            estado: '',
            cep: ''
          },
          responsavel_legal: '',
          inscricao_estadual: '',
          inscricao_municipal: ''
        });
        setEtapaAtual(1);
      } else {
        setErro(result.data.message || 'Erro ao criar empresa');
      }
    } catch (error) {
      console.error('❌ Erro ao criar empresa:', error);
      
      if (error.code === 'functions/already-exists') {
        setErro('CNPJ ou email já cadastrado no sistema');
      } else if (error.code === 'functions/invalid-argument') {
        setErro('Dados inválidos. Verifique os campos e tente novamente.');
      } else if (error.code === 'functions/permission-denied') {
        setErro('Você não tem permissão para criar empresas');
      } else {
        setErro('Erro ao criar empresa. Verifique os dados e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Building2 className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Nova Empresa</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Etapa {etapaAtual} de 2
            </span>
            <span className="text-sm text-gray-500">
              {etapaAtual === 1 && 'Dados da Empresa'}
              {etapaAtual === 2 && 'Confirmação'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(etapaAtual / 2) * 100}%` }}
            ></div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {/* Etapa 1: Dados da Empresa */}
            {etapaAtual === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informações da Empresa</h3>
                
                {/* Nome da Empresa e Nome Fantasia */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      name="nome_empresa"
                      value={formData.nome_empresa}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: Empresa ABC Ltda"
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Fantasia
                    </label>
                    <input
                      type="text"
                      name="nome_fantasia"
                      value={formData.nome_fantasia}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: ABC"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* CNPJ com botão de consulta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CNPJ *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={(e) => {
                        const formatted = formatCNPJ(e.target.value);
                        if (formatted.replace(/\D/g, '').length <= 14) {
                          handleInputChange({ target: { name: 'cnpj', value: formatted } });
                          setErroCNPJ('');
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="00.000.000/0000-00"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={handleConsultarCNPJ}
                      disabled={consultandoCNPJ || loading || formData.cnpj.replace(/\D/g, '').length !== 14}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      {consultandoCNPJ ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Consultando...</span>
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          <span>Consultar</span>
                        </>
                      )}
                    </button>
                  </div>
                  {erroCNPJ && (
                    <p className="text-red-500 text-xs mt-1">{erroCNPJ}</p>
                  )}
                  {consultandoCNPJ && (
                    <p className="text-blue-600 text-xs mt-1">Buscando dados na Receita Federal...</p>
                  )}
                </div>

                {/* E-mail da Empresa */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail da Empresa *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="contato@empresa.com"
                    required
                    disabled={loading}
                  />
                </div>

                {/* E-mail Financeiro */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail Financeiro - Contas a Pagar
                  </label>
                  <input
                    type="email"
                    name="email_financeiro"
                    value={formData.email_financeiro}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="financeiro@empresa.com"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    E-mail que receberá as faturas do QualiManager
                  </p>
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="text"
                    name="telefone"
                    value={formData.telefone}
                    onChange={(e) => {
                      const formatted = formatTelefone(e.target.value);
                      if (formatted.replace(/\D/g, '').length <= 11) {
                        handleInputChange({ target: { name: 'telefone', value: formatted } });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(11) 99999-9999"
                    required
                    disabled={loading}
                  />
                </div>

                {/* ✅ NOVOS CAMPOS: Administrador da Empresa */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Administrador da Empresa
                  </h4>
                  <p className="text-sm text-blue-700">
                    O administrador receberá um email para ativar sua conta no sistema.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Administrador *
                      </label>
                      <input
                        type="text"
                        name="admin_nome"
                        value={formData.admin_nome}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="João Silva"
                        required
                        disabled={loading}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-mail do Administrador *
                      </label>
                      <input
                        type="email"
                        name="admin_email"
                        value={formData.admin_email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="admin@empresa.com"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900 flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Endereço
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Logradouro *
                      </label>
                      <input
                        type="text"
                        name="endereco.logradouro"
                        value={formData.endereco.logradouro}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Rua, Avenida, etc."
                        required
                        disabled={loading}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número *
                      </label>
                      <input
                        type="text"
                        name="endereco.numero"
                        value={formData.endereco.numero}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="123"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Complemento
                      </label>
                      <input
                        type="text"
                        name="endereco.complemento"
                        value={formData.endereco.complemento}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Sala, Andar, etc."
                        disabled={loading}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bairro *
                      </label>
                      <input
                        type="text"
                        name="endereco.bairro"
                        value={formData.endereco.bairro}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Centro"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cidade *
                      </label>
                      <input
                        type="text"
                        name="endereco.cidade"
                        value={formData.endereco.cidade}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="São Paulo"
                        required
                        disabled={loading}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estado *
                      </label>
                      <select
                        name="endereco.estado"
                        value={formData.endereco.estado}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        disabled={loading}
                      >
                        <option value="">Selecione</option>
                        {estados.map(estado => (
                          <option key={estado} value={estado}>{estado}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CEP *
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="endereco.cep"
                          value={formData.endereco.cep}
                          onChange={(e) => {
                            const formatted = formatCEP(e.target.value);
                            if (formatted.replace(/\D/g, '').length <= 8) {
                              handleInputChange({ target: { name: 'endereco.cep', value: formatted } });
                              setErroCEP('');
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="00000-000"
                          required
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={handleConsultarCEP}
                          disabled={consultandoCEP || loading || formData.endereco.cep.replace(/\D/g, '').length !== 8}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                        >
                          {consultandoCEP ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Buscando...</span>
                            </>
                          ) : (
                            <>
                              <Search className="h-4 w-4" />
                              <span>Buscar</span>
                            </>
                          )}
                        </button>
                      </div>
                      {erroCEP && (
                        <p className="text-red-500 text-xs mt-1">{erroCEP}</p>
                      )}
                      {consultandoCEP && (
                        <p className="text-blue-600 text-xs mt-1">Buscando endereço via CEP...</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Campos Opcionais */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Informações Adicionais (Opcional)</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Responsável Legal
                    </label>
                    <input
                      type="text"
                      name="responsavel_legal"
                      value={formData.responsavel_legal}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nome do responsável legal"
                      disabled={loading}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inscrição Estadual
                      </label>
                      <input
                        type="text"
                        name="inscricao_estadual"
                        value={formData.inscricao_estadual}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="000.000.000.000"
                        disabled={loading}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inscrição Municipal
                      </label>
                      <input
                        type="text"
                        name="inscricao_municipal"
                        value={formData.inscricao_municipal}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="000000000"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Etapa 2: Confirmação */}
            {etapaAtual === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Send className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Confirmar Criação</h3>
                  <p className="text-gray-600">
                    Revise os dados antes de criar a empresa. O e-mail da empresa será usado para criar o administrador.
                  </p>
                </div>

                {/* Resumo da Empresa */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Building2 className="h-4 w-4 mr-2" />
                    Dados da Empresa
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Nome:</span>
                      <span className="ml-2 font-medium">{formData.nome_empresa}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Nome Fantasia:</span>
                      <span className="ml-2 font-medium">{formData.nome_fantasia || 'Não informado'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">CNPJ:</span>
                      <span className="ml-2 font-medium">{formData.cnpj}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">E-mail:</span>
                      <span className="ml-2 font-medium">{formData.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">E-mail Financeiro:</span>
                      <span className="ml-2 font-medium">{formData.email_financeiro || 'Não informado'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Telefone:</span>
                      <span className="ml-2 font-medium">{formData.telefone}</span>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-gray-600">Endereço:</span>
                      <span className="ml-2 font-medium">
                        {formData.endereco.logradouro}, {formData.endereco.numero}
                        {formData.endereco.complemento && `, ${formData.endereco.complemento}`} - 
                        {formData.endereco.bairro}, {formData.endereco.cidade}/{formData.endereco.estado} - 
                        {formData.endereco.cep}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Resumo do Administrador */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Administrador da Empresa
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Nome:</span>
                      <span className="ml-2 font-medium">{formData.admin_nome}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">E-mail:</span>
                      <span className="ml-2 font-medium">{formData.admin_email}</span>
                    </div>
                  </div>
                  <p className="text-sm text-blue-700 mt-3">
                    Um convite será enviado automaticamente para <strong>{formData.admin_email}</strong> 
                    para que {formData.admin_nome.split(' ')[0]} possa ativar sua conta.
                  </p>
                </div>
              </div>
            )}

            {/* Erro */}
            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-4">
                <div className="flex">
                  <XCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{erro}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <div className="flex space-x-3">
              {etapaAtual > 1 && (
                <button
                  type="button"
                  onClick={etapaAnterior}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              
              {etapaAtual < 2 ? (
                <button
                  type="button"
                  onClick={proximaEtapa}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Criando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Criar Empresa
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaEmpresaModal;

